import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7oq173hiaolq192k2g";

interface FormApiResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("反馈已提交，感谢你的建议！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormApiResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormApiResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const succeeded = response.ok && parsed?.code === "OK" && !isSpam;

      if (succeeded) {
        setStatus("success");
        setFeedback("反馈已提交，感谢你的建议！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复的邮箱"
            className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="type"
          defaultValue="内容建议"
          className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="资源导航">资源导航</option>
          <option value="功能反馈">功能反馈</option>
          <option value="版权问题">版权问题</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-verify-alt"
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}`}></i>
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        {feedback ? (
          <p className={`text-sm ${status === "success" ? "text-primary-600" : "text-accent-700"}`}>
            {feedback}
          </p>
        ) : null}
      </div>
    </form>
  );
}